import { useEffect, useState } from 'react';
import type { SpeakingEvent } from '../identity/DiscordSdkPort.js';

/**
 * Bridges Discord SPEAKING_START/STOP edges to the ReadonlySet that
 * SpeakingProvider consumes. Speaking is CLIENT-LOCAL state (every client in
 * the voice channel receives everyone's edges from its own Discord RPC
 * socket) — it never touches the Colyseus schema. See SpeakingContext.tsx.
 *
 * `source` is null outside Discord (dev-real): the set stays empty and no
 * subscription is attempted. The caller also skips mounting the provider in
 * that case, keeping the float inert exactly as before this wiring.
 */

/** The slice of DiscordSdkPort the hook consumes — structural, so tests can
 *  pass any object with a compatible subscribeSpeaking. */
export interface SpeakingEventSource {
  subscribeSpeaking(
    listener: (event: SpeakingEvent) => void,
  ): Promise<() => Promise<void>>;
}

const EMPTY: ReadonlySet<string> = new Set<string>();

export function useDiscordSpeaking(
  source: SpeakingEventSource | null,
): ReadonlySet<string> {
  const [speakingIds, setSpeakingIds] = useState<ReadonlySet<string>>(EMPTY);

  useEffect(() => {
    if (source === null) return undefined;
    // A new source starts from a clean slate — stale members from a previous
    // source would otherwise linger until an edge for that id arrives.
    setSpeakingIds(EMPTY);
    let cancelled = false;
    let unsubscribe: (() => Promise<void>) | null = null;

    source
      .subscribeSpeaking(({ userId, speaking }) => {
        setSpeakingIds((prev) => {
          // No-op edges (duplicate START, STOP for a non-member) keep the
          // same Set instance so SpeakingProvider's memo doesn't churn.
          if (prev.has(userId) === speaking) return prev;
          const next = new Set(prev);
          if (speaking) next.add(userId);
          else next.delete(userId);
          return next;
        });
      })
      .then((unsub) => {
        // teardown unsubscribe may reject while the RPC socket closes — not actionable
        if (cancelled) {
          unsub().catch(() => {});
        } else unsubscribe = unsub;
      })
      .catch((err: unknown) => {
        // Boot's single authorize call granted rpc.voice.read or we wouldn't
        // be here, so a rejection is transient/unexpected. The float is
        // atmosphere — degrade silently rather than surface an error.
        console.warn('[voice] speaking subscription failed; float stays inert', err);
      });

    return () => {
      cancelled = true;
      if (unsubscribe !== null) unsubscribe().catch(() => {});
    };
  }, [source]);

  return speakingIds;
}
